import React, { useCallback, useEffect, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import FinanceBreadcrumbs from './components/FinanceBreadcrumbs';
import PaymentPanel from './components/Payments/PaymentPanel';
import ChildPaymentsModal from './components/ChildPaymentsModal';
import ScopeOverview from './components/Overview/ScopeOverview';
import ChildrenMonthTable from './components/Overview/ChildrenMonthTable';
import GroupSchemeCard from './components/Overview/GroupSchemeCard';
import GroupSchemeDrawer from './components/Overview/GroupSchemeDrawer';
import ExportMenu from './components/Overview/ExportMenu';
import { scopeGate } from './components/Overview/scopeGate';
import { forMonth } from './components/Overview/monthText';
import { OverviewScope } from './components/Overview/scope';
import { useFinanceKindergarten } from './useFinanceKindergarten';
import { useFinanceMonth } from './useFinanceMonth';
import { useFinanceSummary } from './useFinanceSummary';
import { useFinanceComposition, useFinanceToday } from './useFinanceToday';
import { backState } from '../../components/BackLink';
import { contractHref, FINANCE } from '../../common/constants/routes.constants';
import { FeatureCode } from '../../common/enums/common.enum';
import { FinanceBreakdownRowDto, FinanceGroupSchemeDto } from '../../common/interfaces/invoicing.interface';
import { getFinanceGroupScheme } from '../../api/invoicing.api';
import { useKindergartenFeatures } from '../../utilities/hooks/useKindergartenFeatures';

/**
 * One group, laid out as the overview, broken down by child.
 *
 * The child rows come from the same aggregation as every row above them, not from the
 * debts endpoint: that one attributes a child to its *current* group, while the group's
 * figures come from the group on each invoice. The debt in a child's row is the child's in
 * full, as on Debts.
 *
 * Clicking a child opens the payment panel, which is where the chasing actually ends.
 */
const FinanceGroupPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { locationId = '', groupId = '' } = useParams<{ locationId: string; groupId: string }>();
  const { kindergartenId, kindergartenName } = useFinanceKindergarten();
  const [month, setMonth] = useFinanceMonth();
  const { isEnabled, isLoading: isLoadingFeatures } = useKindergartenFeatures(kindergartenId || undefined);
  const enabled = !!kindergartenId && !isLoadingFeatures && isEnabled(FeatureCode.INVOICING);
  // bumped after a payment or a price list is saved, so every figure is fetched again
  const [reloadKey, setReloadKey] = useState(0);
  const reload = useCallback(() => setReloadKey(key => key + 1), []);
  const { summary, trend, isLoading, failed } = useFinanceSummary({
    kindergartenId: enabled ? kindergartenId : '',
    month,
    groupId,
    trendMonths: 12,
    reloadKey
  });
  const { todo, buckets } = useFinanceToday({ kindergartenId, enabled, groupId, month, reloadKey });
  const composition = useFinanceComposition({ kindergartenId, enabled, groupId, month, reloadKey });
  const [scheme, setScheme] = useState<FinanceGroupSchemeDto | null | undefined>(undefined);
  const [paying, setPaying] = useState<FinanceBreakdownRowDto | null>(null);
  const [history, setHistory] = useState<FinanceBreakdownRowDto | null>(null);
  const [editingScheme, setEditingScheme] = useState(false);
  const closeScheme = useCallback(() => setEditingScheme(false), []);

  useEffect(() => {
    if (!enabled || !groupId) return undefined;
    let cancelled = false;
    getFinanceGroupScheme(kindergartenId, groupId)
      .then(result => !cancelled && setScheme(result.results ?? null))
      .catch(() => !cancelled && setScheme(null));
    return () => {
      cancelled = true;
    };
  }, [kindergartenId, groupId, enabled, reloadKey]);

  const name = summary?.scope.groupName ?? '';
  const children = summary?.breakdown.reduce((sum, row) => sum + row.childCount, 0) ?? 0;
  const scope: OverviewScope = { kind: 'group', groupId, name, locationId };
  const here = `${location.pathname}${location.search}`;

  const gate = scopeGate(t, {
    kindergartenId,
    loading: isLoadingFeatures || (isLoading && !summary),
    invoicingOn: isEnabled(FeatureCode.INVOICING),
    failed: failed || !summary
  });

  return (
    <Box sx={{ p: 7 }}>
      <FinanceBreadcrumbs
        kindergartenId={kindergartenId}
        locationId={locationId}
        groupId={groupId}
        fallbackNames={{ location: summary?.scope.locationName, group: summary?.scope.groupName }}
      />
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ md: 'flex-start' }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h2">{name || t('common.group')}</Typography>
          {summary && (
            <Typography sx={{ fontSize: 14, color: 'text.secondary', mt: 0.5 }}>
              {[summary.scope.locationName, t('finance.scope.children', { count: children })].filter(Boolean).join(' · ')}
              {scheme !== undefined && (
                <>
                  {' · '}
                  {scheme ? (
                    t('finance.scope.scheme', { name: scheme.name })
                  ) : (
                    <Box component="span" sx={{ color: 'warning.main' }}>
                      {t('finance.groups.noScheme')}
                    </Box>
                  )}
                </>
              )}
            </Typography>
          )}
        </Box>
        {enabled && <ExportMenu kindergartenId={kindergartenId} kindergartenName={kindergartenName ?? ''} month={month} scope={scope} />}
      </Stack>
      {gate ??
        (summary && (
          <ScopeOverview
            kindergartenId={kindergartenId}
            scope={scope}
            month={month}
            onMonthChange={setMonth}
            summary={summary}
            trend={trend}
            todo={todo}
            buckets={buckets}
            composition={composition}
            onOpenScheme={() => setEditingScheme(true)}
            aside={
              scheme !== undefined ? (
                <GroupSchemeCard scheme={scheme} kindergartenId={kindergartenId} groupId={groupId} onEdit={() => setEditingScheme(true)} />
              ) : (
                <Box />
              )
            }
            tableTitle={t('finance.overview.byChild', { month: forMonth(t, month) })}
            table={
              <ChildrenMonthTable
                rows={summary.breakdown}
                monthLabel={forMonth(t, month)}
                totalLabel={t('finance.scope.total', { name })}
                actions={{
                  onPay: setPaying,
                  onHistory: setHistory,
                  onOpenInvoice: invoiceId =>
                    navigate(`${FINANCE}/invoice/${invoiceId}?kindergartenId=${kindergartenId}`, backState(here, name)),
                  onOpenContract: childId => navigate(contractHref(kindergartenId, childId))
                }}
              />
            }
          />
        ))}

      {paying && (
        <PaymentPanel
          kindergartenId={kindergartenId}
          childId={paying.id as string}
          childName={paying.name ?? ''}
          payerName={paying.child?.payerFullName ?? undefined}
          onClose={() => setPaying(null)}
          onRecorded={reload}
        />
      )}
      {history && (
        <ChildPaymentsModal
          kindergartenId={kindergartenId}
          childId={history.id as string}
          childName={history.name ?? ''}
          outstanding={history.child?.outstanding ?? 0}
          onClose={() => setHistory(null)}
          onChanged={reload}
        />
      )}
      {editingScheme && (
        <GroupSchemeDrawer
          kindergartenId={kindergartenId}
          groupId={groupId}
          schemeId={scheme?.schemeId ?? null}
          onClose={closeScheme}
          onSaved={reload}
        />
      )}
    </Box>
  );
};

export default FinanceGroupPage;
